import React, { useEffect } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import BarRoundedChart from '../../../../../components/ui/charts/BarRoundedChart';
import DonutChart from '../../../../../components/ui/charts/DonutChart';
import ProgressDonutChart from '../../../../../components/ui/charts/ProgressDonutChart';
import CurrencyText from '../../../../../components/ui/labels/CurrencyText';
import { Banknote, Building2, Camera, HandCoins } from 'lucide-react';
import DonutChart2 from '../../../../../components/ui/charts/DonutChart2';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import RetirementScenarioCard from '../../../../../components/cards/RetirementScenarioCard';
import RetirementProjectionChart from '../../../../../components/ui/charts/RetirementProjectionChart';
import DreamInsight from '../../../../../components/ui/labels/DreamInsight';
import BarRoundedChartStack from '../../../../../components/ui/charts/BarRoundedChartStack';
import CustomActionButton from '../../../../../components/ui/buttons/CustomActionButton';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';
import BlurScopeContent from '../../../../../components/ui/overlays/BlurScopeContent';
import { retirementChartDataMock } from '../../../../../utils/mocks/retirementChartDataMock';

const OverviewTab = ({ clientId, scope }) => {
  const { addToast } = useToast();

  const mockData = [
    { label: 'Jan / 2025', value: 25000 },
    { label: 'Fev / 2025', value: 20000 },
    { label: 'Mar / 2025', value: 23000 },
    { label: 'Abr / 2025', value: 24000 },
    { label: 'Mai / 2025', value: 8000 },
    { label: 'Jun / 2025', value: 16000 },
    { label: 'Jul / 2025', value: 3000 },
    { label: 'Ago / 2025', value: 26000 },
  ];

  const mockData2 = [
    { name: 'Saldos', percent: 59 },
    { name: 'Criptoativos', percent: 28 },
    { name: 'Créditos', percent: 12 },
    { name: 'Outros', percent: 5 },
  ];

  const [data, setData] = React.useState({
    totalProperties: 0,
    totalFixedProperties: 0,
    totalFinancialProperties: 0,
    btgTotal: 0,
    btgPensionTotal: 0,
    btgTotalWithoutPension: 0,
    deathProtectionsTotal: 0,
    deathProtectionsPercent: 0,
    pensionPercent: 0,
    inventoryFree: 0,
    monthlyContributions: [],
    currentYear: '',
    retirementChartData: [],
    currentMonthContributions: 0,
    totalContributions: 0,
    currentYearContributions: 0,
    investmentsChartData: [],
    insuranceTotal: 0,
    insurancePercent: 0,
    patrimonyChartData: [],
    pensionTotal: 0,
  });

  const [isRegisteringMonth, setIsRegisteringMonth] = React.useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const onSuccess = () => {
    fetchData();
  };

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/overview?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  const handleRegisterMonth = async () => {
    try {
      if (isRegisteringMonth) return;
      setIsRegisteringMonth(true);
      const payload = {};

      const res = await fetchWithCsrf(
        `/app/planner/client/${clientId}/dashboard/overview/register_patrimony_month?scope=${scope}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Falha ao registrar o mês');
      }

      addToast({ type: 'success', message: 'Mês registrado/atualizado com sucesso.' });
      onSuccess();
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', message: 'Não foi possível registrar o mês. Tente novamente.' });
    } finally {
      setIsRegisteringMonth(false);
    }
  };

  const insightTypes = ['ok', 'problem', 'warning'];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-10 gap-5">
        <div className="col-span-7 flex gap-5">
          {/* Patrimônio */}
          <div className="flex flex-col gap-5 flex-1">
            <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
              <h2 className="text-lg text-blueDarker font-semibold">Patrimônio total</h2>
              <CurrencyText size="large" value={data.totalProperties} />
            </div>
            <div className="flex gap-5 h-full">
              <div className="bg-white rounded-custom20 w-full px-6 py-4">
                <div className="flex gap-2 items-center mb-2">
                  <Banknote className="text-tealMedium" size={20} strokeWidth={1.5} />
                  <h2 className="text-lg text-blueDarker font-semibold">Financeiro</h2>
                </div>

                <CurrencyText size="small" value={data.totalFinancialProperties} color="text-tealMedium" />
              </div>
              <div className="bg-white rounded-custom20 w-full px-6 py-4">
                <div className="flex gap-2 items-center mb-2">
                  <Building2 className="text-tealMedium" size={20} strokeWidth={1.5} />
                  <h2 className="text-lg text-blueDarker font-semibold">Imobilizado</h2>
                </div>
                <CurrencyText size="small" value={data.totalFixedProperties} color="text-tealMedium" />
              </div>
            </div>
          </div>

          {/* Gráfico investimentos + detalhes */}
          <div className="bg-white rounded-custom20 pr-6 grid grid-cols-2 gap-4 items-center flex-1">
            <div className="">
              <DonutChart2 data={data.investmentsChartData} height={220} />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-lg text-blueDarker font-semibold">Investimentos (BTG)</h2>
              <CurrencyText size="large" value={data.btgTotalWithoutPension} color="text-tealMedium" />
              <h2 className="text-lg text-blueDarker font-semibold">Previdência (BTG)</h2>
              <CurrencyText size="large" value={data.btgPensionTotal} color="text-tealMedium" />
            </div>
          </div>
        </div>
        {/* Patrimônio livre de invetario */}
        <div className="bg-white rounded-custom20 py-3 col-span-3 grid grid-cols-2 items-center px-16">
          <ProgressDonutChart percentage={parseFloat(data.pensionPercent)} label="Previdência" />
          <ProgressDonutChart percentage={parseFloat(data.insurancePercent)} label="Seguro de vida" />
          <div className="col-span-2 flex flex-col items-center mt-2">
            <h2 className="text-lg text-blueDarker font-semibold">Patrimônio livre de inventário</h2>
            <CurrencyText size="large" value={data.inventoryFree} color="text-blueHighlight" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-10 gap-5">
        <div className="bg-white rounded-custom20 p-6 col-span-7 flex flex-col gap-4" style={{ height: '460px' }}>
          <h2 className="text-lg text-blueDarker font-semibold">Independência financeira</h2>
          {scope === 'couple' ? (
            <BlurScopeContent
              title='Indisponível para a visualização "casal"'
              subtitle="Altere a visualização para Cliente ou Cônjuge para ver as projeções."
              className="flex-1"
            >
              <RetirementProjectionChart scenarios={retirementChartDataMock} />
            </BlurScopeContent>
          ) : data.retirementChartData.length > 0 ? (
            <RetirementProjectionChart scenarios={data.retirementChartData} />
          ) : (
            <span className="text-sm text-blueDark">
              Nenhum cenário de independência financeira cadastrado como prioridade.
            </span>
          )}
        </div>

        <div className="flex flex-col col-span-3 gap-5" style={{ height: '460px' }}>
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2 h-full">
            <h2 className="text-lg text-blueDarker font-semibold">Insights</h2>

            <div className="flex flex-col gap-3 overflow-y-auto">
              {scope === 'couple' ? (
                <div className="relative flex-1">
                  <BlurScopeContent
                    title='Indisponível para a visualização "casal"'
                    subtitle="Altere a visualização para Cliente ou Cônjuge para ver os insights."
                    className="absolute inset-0"
                  >
                    <div className="flex flex-col gap-3 overflow-y-auto">
                      {retirementChartDataMock.length > 0 ? (
                        insightTypes.some((type) => retirementChartDataMock[0].insights?.[type]?.length > 0) ? (
                          insightTypes.map((type) => {
                            const items = retirementChartDataMock[0].insights?.[type] || [];
                            if (items.length === 0) return null;

                            return (
                              <div key={type} className="flex flex-col gap-1">
                                {items.map((text, idx) => (
                                  <DreamInsight
                                    key={`${retirementChartDataMock[0].scenarioId}-${type}-${idx}`}
                                    type={type}
                                    text={text}
                                  />
                                ))}
                              </div>
                            );
                          })
                        ) : (
                          <></>
                        )
                      ) : (
                        <span className="text-sm text-blueDark">
                          Nenhum cenário de independência financeira cadastrado como prioridade.
                        </span>
                      )}
                    </div>
                  </BlurScopeContent>
                </div>
              ) : (
                <div className="flex flex-col gap-3 overflow-y-auto">
                  {data.retirementChartData.length > 0 ? (
                    insightTypes.some((type) => data.retirementChartData[0].insights?.[type]?.length > 0) ? (
                      insightTypes.map((type) => {
                        const items = data.retirementChartData[0].insights?.[type] || [];
                        if (items.length === 0) return null;

                        return (
                          <div key={type} className="flex flex-col gap-1">
                            {items.map((text, idx) => (
                              <DreamInsight
                                key={`${data.retirementChartData[0].scenarioId}-${type}-${idx}`}
                                type={type}
                                text={text}
                              />
                            ))}
                          </div>
                        );
                      })
                    ) : (
                      <></>
                    )
                  ) : (
                    <span className="text-sm text-blueDark">
                      Nenhum cenário de independência financeira cadastrado como prioridade.
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-10 gap-5 col-span-10">
          <div className="bg-white rounded-custom20 p-6 col-span-10 flex flex-col h-90" style={{ height: '460px' }}>
            <div className="flex justify-between items-center">
              <h2 className="text-lg text-blueDarker font-semibold">Evolução do Patrimônio</h2>
            </div>
            <div className="" style={{ maxHeight: '460px' }}>
              <BarRoundedChartStack
                data={data.patrimonyChartData}
                labelKey="label"
                series={[
                  { key: 'totalFixedProperties', label: 'Imobilizado', color: '#2d6386ff' },
                  { key: 'totalFinancialProperties', label: 'Financeiro (outros)', color: '#2D86B4' },
                  // { key: 'totalPension', label: 'Previdência', color: '#7AC3E0' },
                  // { key: 'totalInsurances', label: 'Seguros', color: '#9FD4E9' },
                  { key: 'totalBtg', label: 'Financeiro (BTG)', color: '#5BB2D7' },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-10 gap-5 col-span-10">
          <div className="bg-white rounded-custom20 p-6 col-span-7 flex flex-col gap-4" style={{ height: '460px' }}>
            <h2 className="text-lg text-blueDarker font-semibold">Aportes mensais / {data.currentYear}</h2>
            <BarRoundedChart data={data.monthlyContributions} className="h-full" />
          </div>
          <div className="flex flex-col gap-5 h-full col-span-3 justify-between" style={{ height: '460px' }}>
            <div className="bg-white rounded-custom20 w-full py-6 flex flex-col gap-2 items-center justify-center h-full">
              <HandCoins className="text-tealMedium" size={42} strokeWidth={1.25} />
              <h2 className="text-lg text-blueDarker font-semibold">Aportes de {data.currentYear}</h2>
              <CurrencyText size="large" value={data.currentYearContributions} />
            </div>
            <div className="bg-white rounded-custom20 w-full py-6 flex flex-col gap-2 items-center justify-center h-full">
              <Banknote className="text-tealMedium" size={42} strokeWidth={1.25} />
              {/* <h2 className="text-lg text-blueDarker font-semibold">Aportes totais</h2>
              <CurrencyText size="large" value={data.totalContributions} /> */}
              <h2 className="text-lg text-blueDarker font-semibold">Média dos Aportes</h2>
              <CurrencyText size="large" value={data.averageContribution} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
